import { sharedCookieDomain } from './lang';

/**
 * Cross-app theme persistence.
 *
 * Deliberately the same shape as `lang.ts`, and it reuses that module's `sharedCookieDomain()`: the
 * apps live on sibling subdomains, so per-origin localStorage never carries a choice between them.
 * A cookie on the shared parent domain does. Someone who picks dark in the clinic app and follows a
 * link into the help site should not arrive at a white page.
 *
 * Two values are distinct here and it matters:
 *   - the *preference*, which is what the user picked and may be `system`; stored in the cookie.
 *   - the *resolved* theme, only ever `light` or `dark`; written to `data-theme` on <html>.
 *
 * Keeping `system` out of the DOM is what lets the CSS carry a single `[data-theme='dark']` block
 * rather than duplicating the whole palette inside a `prefers-color-scheme` query.
 */

export const SHARED_THEME_COOKIE = 'edtheme';

const PREFS = ['light', 'dark', 'system'] as const;
export type ThemePref = (typeof PREFS)[number];
export type ResolvedTheme = 'light' | 'dark';

const isPref = (v: unknown): v is ThemePref =>
  typeof v === 'string' && (PREFS as readonly string[]).includes(v);

const DARK_QUERY = '(prefers-color-scheme: dark)';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

/** What the user asked for: `?theme=` first, then the shared cookie, then `system`. */
export function getThemePref(): ThemePref {
  try {
    const q = new URLSearchParams(location.search).get('theme');
    if (isPref(q)) return q;
  } catch { /* no location */ }
  const c = readCookie(SHARED_THEME_COOKIE);
  return isPref(c) ? c : 'system';
}

/** Does the OS currently want dark? False anywhere `matchMedia` is unavailable. */
export function systemPrefersDark(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
}

export function resolveTheme(pref: ThemePref): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

/** Write the resolved theme to <html>. The CSS keys off this and nothing else. */
export function applyTheme(pref: ThemePref): ResolvedTheme {
  const resolved = resolveTheme(pref);
  try {
    document.documentElement.dataset.theme = resolved;
  } catch { /* no document */ }
  return resolved;
}

/** Persist the choice for every app on the parent domain, and apply it here immediately. */
export function persistTheme(pref: ThemePref): ResolvedTheme {
  if (!isPref(pref)) return applyTheme(getThemePref());
  try {
    localStorage.setItem('theme', pref);
  } catch { /* storage blocked */ }
  try {
    const dom = sharedCookieDomain();
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; secure' : '';
    document.cookie =
      `${SHARED_THEME_COOKIE}=${pref}; path=/; max-age=31536000; samesite=lax${secure}${dom ? `; domain=${dom}` : ''}`;
  } catch { /* cookies blocked */ }
  return applyTheme(pref);
}

/**
 * Keep the page in step with the OS while the preference is `system`.
 *
 * The listener stays subscribed whatever the preference is, and re-reads it on each change, so a
 * user sitting on `system` at dusk sees the app follow the machine without a reload. Returns an
 * unsubscribe for React's effect cleanup.
 */
export function watchSystemTheme(onChange?: (resolved: ResolvedTheme) => void): () => void {
  let mq: MediaQueryList;
  try {
    if (typeof matchMedia !== 'function') return () => {};
    mq = matchMedia(DARK_QUERY);
  } catch {
    return () => {};
  }

  const handler = () => {
    if (getThemePref() !== 'system') return;
    onChange?.(applyTheme('system'));
  };

  // Safari below 14 only has the deprecated form; the app still has to work there.
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', handler);
  else mq.addListener(handler);

  return () => {
    if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', handler);
    else mq.removeListener(handler);
  };
}

/**
 * Append the chosen theme to a cross-app URL.
 *
 * The `edtheme` cookie covers subdomains of one site, which is most navigation. It cannot cross a
 * registrable-domain boundary, and the public site is reachable on a different one from the apps —
 * so someone who picks dark on the marketing site and clicks "Sign in" would otherwise land on a
 * white login page. `?theme=` closes that hop, exactly as `withLang` does for language.
 *
 * Carries the *preference*, not the resolved theme: forwarding `system` as `dark` would pin a
 * choice the user never made.
 */
export function withTheme(url: string, pref?: ThemePref): string {
  const p = isPref(pref) ? pref : getThemePref();
  try {
    const u = new URL(url, typeof location !== 'undefined' ? location.href : 'https://x');
    u.searchParams.set('theme', p);
    return u.toString();
  } catch {
    return url + (url.includes('?') ? '&' : '?') + 'theme=' + p;
  }
}
